// src/encounter/structured-note/inline-note-line.tsx
//
// PROD-3043 · frame v2 only (design desk D-73, 6 Oct; encounter UX laws L6 and L7). A narrative the
// clinician types INLINE, as a note line (D-61): the DS NoteFieldLine, which grows with the text, takes
// ". phrase" and "/ insert", and holds plain text. It replaces the "Add Text Notes" pop-up (a Quill
// word processor over the note) for a narrative the host stores OUTSIDE the template answers — today
// the A&P narrative (`ap-inline-narrative.tsx`). Template narratives keep their own line
// (structured-note-section.tsx `narrative`), which writes the QuestionnaireResponse.
//
// TYPED TEXT IS NEVER LOST (L7). The line keeps a local draft while the clinician types and hands it to
// the host — whose autosave persists it — when typing pauses (`INLINE_NOTE_SAVE_MS`), when focus leaves
// the line, when the line unmounts (a section switch, leaving the visit), and when the page is hidden or
// unloaded. The prompt before discarding is lane A's (D-73: one prompt, ON and OFF); this file builds
// none, and keeps its draft out of browser storage (it is patient data).
//
// ANOTHER WRITER WHILE TYPING. A change to the stored line that arrives while the clinician has a draft
// (ambient appending "Suspected: …", or its Discard taking that line back out) is applied to the draft too
// (`rebaseExternal`), pieces included; a change that cannot be placed leaves the draft as typed.
//
// NEVER INTO ANOTHER RECORD. The draft is bound to the patient and visit it was typed for (`owner`). If
// the line is re-rendered for a different record before it is saved, the draft is not written there
// (contract G5's rule for the dialog, applied to the line); a draft is held, not written, while the line
// points at a record whose ids are not known, and delivered as soon as they are (no edit or blur needed). The page is keyed
// by appointment (encounter-route.tsx), so in the app a record switch remounts the line; this is a guard.
//
// PHRASES are the same library the template lines use (note-phrases.ts): a phrase without pieces lands
// at the cursor (NoteFieldLine's own insert); a phrase with pieces (`pieces`, the `?ef=phrasePieces`
// switch) opens the fill panel under the line, and its completed pieces land where `.` was typed (PROD-3148).
//
// CHART READOUT (`?x=chart-readout`, ON on the dev hosts; Antonin's catalog ChartReadout @166c3c35). "\" typed at the end of the
// text lists the chart data the line's `chartReads` already hold (lib/chart-readout.ts); a pick takes the "\" off and lands a
// read-only readout under the text, saved at once. The readouts are chart pieces at the end of the stored segments: the field
// edits only the text before them, and a readout leaves only by its X. Switch off: "\" is plain text and opens nothing, but
// readouts already stored still show as cards (never as editable text, which an edit would break).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { NoteFieldLine, NoteLine, type AuthorRole, type CommandMenuItem, type NoteFieldCommands } from '@scripsteam/scrips-signal-ds';
import type { NoteMacro } from '../lib/smart-phrase';
import { resolveChart, type ChartReads } from '../lib/to-chart-snapshot';
import type { ChartPiece } from '../lib/note-pieces';
import { insertPhrase, rebaseSegs, segsText, type LineSeg } from '../lib/note-line-delta';
import { otherRecord, rebaseExternal, sameRecord } from '../lib/narrative-line-wire';
import type { NoteWhere } from '../lib/note-pieces';
import { carriesPieces, lineCommandItems, offeredPhrasesIn, phrasePlainText, placeAt, toCommandItems, triggerAnchorIn } from './note-phrases';
import { startPhraseFill, withFreshCharts, withRefreshedChart, type PhraseFillDraft } from './phrase-fill-model';
import { useAddNoteAction, useHasAddNoteSlot, useNoteSigned } from './note-add-action';
import { PhraseOccurrence } from './phrase-occurrence';
import { useInlineDraftFlush, useUnsavedNote } from '../services/unsaved-note';
import { SIGNED_NOT_SAVED } from '../lib/signed-refusal';
import { suggestedFirst } from '../complaint-map/phrases-first';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { READOUT_TRIGGER, joinReadouts, newReadout, readoutSources, splitReadouts, withoutTrigger, type ReadoutSource } from '../lib/chart-readout';
import { ChartReadoutCard, ChartReadoutMenu } from './chart-readout-parts';

/** How long typing must pause before the draft goes to the host's autosave. */
export const INLINE_NOTE_SAVE_MS = 500;

export interface InlineNoteLineProps {
  /** The field's DOM id (focus target after a phrase completes). */
  id: string;
  label: string;
  /** The stored line: its text, with the pieces the stored value still derives. */
  segs: readonly LineSeg[];
  /** The draft, handed to the host's autosave (pause, blur, unmount, page hidden). */
  onCommit: (segs: LineSeg[]) => void;
  /** Frozen (rule 38: a provider signature; or the host not ready): the text only, no phrases. */
  readOnly?: boolean;
  /** The phrase library on offer. Absent → the field takes plain text and advertises no menu. */
  macros?: readonly NoteMacro[];
  /** The note editors this line answers to in `cps-phrase.editors` (note-phrases.ts). */
  editors: readonly string[];
  author: AuthorRole;
  /** Piece phrases are offered and filled before they land (`?ef=phrasePieces`). */
  pieces?: boolean;
  chartReads?: ChartReads;
  renderChartPiece?: (piece: ChartPiece) => ReactNode;
  /** Shown under the line (an action from the host, in the value column). */
  footnote?: ReactNode;
  /** The patient and visit the stored line belongs to; a draft is never written into another record. */
  owner?: NoteWhere;
  /** L10 (structure first): `block` — while empty it draws nothing and offers the block's "Add note"
   *  (note-add-action.ts); opened and left empty, it closes again. Absent → always drawn, as before. */
  start?: 'block';
  /** PROD-2947 Part 2 (G8): triggers to suggest first, in this order (the complaint's starter map). Never inserted. */
  suggested?: readonly string[];
}

/** The draft while the clinician types, the stored text it was started from, and whose note it is. */
interface Draft {
  segs: LineSeg[];
  base: string;
  /** The stored segments it was started from, pieces included (a coding-only change keeps the text). */
  baseKey: string;
  owner: NoteWhere;
}

export function InlineNoteLine({
  id,
  label,
  segs,
  onCommit,
  readOnly = false,
  macros,
  editors,
  author,
  pieces = false,
  chartReads,
  renderChartPiece,
  footnote,
  owner,
  start,
  suggested,
}: InlineNoteLineProps) {
  const stored = segsText(segs);
  const storedKey = JSON.stringify(segs);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fill, setFill] = useState<PhraseFillDraft | null>(null);

  // Adjusted while rendering, not in an effect. Re-rendered for another record before the draft was
  // saved: the draft is not this note's and is never written into it. (Ids that only arrive now deliver the
  // draft to that record: the owner effect below.)
  if (draft && otherRecord(draft.owner, owner)) setDraft(null);
  // The stored line changed while the clinician was typing (ambient appended a line, or its Discard took
  // one back out): the same change is applied to the draft, so neither side is lost.
  else if (draft && stored !== draft.base)
    setDraft({ ...draft, segs: rebaseExternal(draft.segs, draft.base, segs) ?? draft.segs, base: stored, baseKey: storedKey });
  // Same text, new coding (a piece added or re-coded under the same words): the clinician's edit is replayed
  // on the stored segments, so the incoming pieces outside it are kept.
  else if (draft && storedKey !== draft.baseKey) setDraft({ ...draft, segs: rebaseSegs(segs, segsText(draft.segs)), baseKey: storedKey });
  // A line that freezes drops an open fill panel (the draft itself is committed by the effect below).
  if (readOnly && fill) setFill(null);

  const current = draft ? draft.segs : segs;
  // `?x=chart-readout`: read once per mount; it only opens the picker. The split is always made: the field edits the text, and the
  // readouts at the end are drawn as cards, so a stored readout is never editable text (a line without one is all `body`).
  const [readoutOn] = useState(() => isEncounterFlagOn('chartReadout'));
  const { body, readouts } = splitReadouts(current);
  const bodyText = segsText(body);
  /** The field's text -> the whole line, the readouts kept after it. */
  const toLine = (text: string): LineSeg[] => joinReadouts(rebaseSegs(body, text), readouts);

  // The latest draft and handler, for the flushes that run outside a render (timer, unmount, page hide).
  const latest = useRef<{ draft: Draft | null; onCommit: (s: LineSeg[]) => void; owner?: NoteWhere; readOnly: boolean }>({ draft, onCommit, owner, readOnly });
  // A layout effect, so a blur or a page hide right after a keystroke already sees that keystroke.
  useLayoutEffect(() => {
    latest.current = { draft, onCommit, owner, readOnly };
  });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Text typed when the line froze (rule 38): held on screen, never written (see the freeze effect below).
  const [held, setHeld] = useState<string | null>(null);
  const heldRef = useRef<string | null>(null);
  useEffect(() => {
    heldRef.current = held;
  });

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const pending = latest.current.draft;
    if (!pending) return;
    // Never after the provider's signature (rule 38; integration, Codex P1): a frozen line writes nothing — its draft is
    // held on screen by the freeze effect below.
    if (latest.current.readOnly) return;
    // Never into another record, nor into one not identified yet (see the header): the draft is HELD until
    // the line points at its own record again (a switch to another record discards it while rendering).
    if (!sameRecord(pending.owner, latest.current.owner)) return;
    latest.current = { ...latest.current, draft: null };
    // A draft that ends as it began (typed, then deleted again) is not a change: nothing is written.
    if (segsText(pending.segs) !== pending.base || JSON.stringify(pending.segs) !== pending.baseKey) latest.current.onCommit(pending.segs);
    setDraft(null);
  }, [setDraft]);

  // PROD-2947 (integration of lanes A and D, D-73 amended): the in-app leave guard flushes every held line BEFORE it
  // decides (encounter-leave-guard.tsx). This line holds typed text up to INLINE_NOTE_SAVE_MS, so it registers its
  // flush: text typed just before Back is committed now, and the guard is told there was something to commit (a
  // draft held for an unidentified record stays held, and counts as unsaved).
  useInlineDraftFlush(`inline-note-line:${id}`, () => {
    // Text held after a freeze is unsaved too (Codex P1, round 2): the guard must ask, never let it vanish.
    const had = latest.current.draft !== null || heldRef.current !== null;
    flush();
    return had;
  });

  // Unmount (a section switch, leaving the visit) and a hidden or unloading page hand the draft over.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onHide);
      flush();
    };
  }, [flush]);

  // Frozen while a draft is open (the note was signed mid-typing). Rule 38 wins over the hand-over (integration, Codex
  // P1): nothing is written after the provider's signature. The signer's own text was committed BEFORE the sign (the
  // page flushes every held line first, useSignAfterFlush); a draft still open here is held on screen, read-only, with
  // the reason — never silently dropped, never written into the signed note (L7 + rule 38).
  // Held text stays on the leave guard's list (Codex P1, round 2): leaving asks "Discard this text?" instead of
  // dropping it with the line. The mark goes with the text (discarded on leave, or reopened below).
  useEffect(() => {
    if (held === null) return;
    const key = `inline-held:${id}`;
    useUnsavedNote.getState().setDraftChanged(key, true);
    return () => useUnsavedNote.getState().setDraftChanged(key, false);
  }, [held, id]);
  // A line that reopens (an amendment that reopens it) takes its held text back as the draft, to save as usual.
  useEffect(() => {
    if (readOnly || heldRef.current === null) return;
    const text = heldRef.current;
    setHeld(null);
    setDraft({ segs: rebaseSegs(latest.current.draft?.segs ?? segs, text), base: segsText(segs), baseKey: JSON.stringify(segs), owner: { ...owner } });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, INLINE_NOTE_SAVE_MS);
    // Only on the reopening itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);
  useEffect(() => {
    if (!readOnly) return;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const pending = latest.current.draft;
    if (!pending) return;
    latest.current = { ...latest.current, draft: null };
    const text = segsText(pending.segs);
    if (text !== pending.base) setHeld(text);
    setDraft(null);
  }, [readOnly]);

  // A draft held while the line pointed at an unidentified record is delivered as soon as it points at its
  // own record again, with no further edit or blur needed (and even if the line froze meanwhile).
  useEffect(() => {
    flush();
  }, [owner?.patientId, owner?.encounterId, flush]);

  const change = (text: string) => {
    // Editing marks the line open: an autosave that empties it never collapses it under the caret; it
    // closes only when focus leaves it empty.
    if (!opened) setOpened(true);
    setDraft(draft ? { ...draft, segs: toLine(text) } : { segs: toLine(text), base: stored, baseKey: storedKey, owner: { ...owner } });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, INLINE_NOTE_SAVE_MS);
  };

  // L10: a block narrative opens from the block's "Add note" (focused) and closes again if left empty.
  const signed = useNoteSigned();
  const hasSlot = useHasAddNoteSlot();
  const [opened, setOpened] = useState(false);
  const focusOnOpen = useRef(false);
  const lineRef = useRef<HTMLDivElement>(null);
  // Only where a block header can offer "Add note"; elsewhere the line is drawn as before.
  // Never while a draft is open: clearing a written line keeps it on screen until focus leaves it.
  const collapsed = start === 'block' && hasSlot && !segsText(current).trim() && !fill && !opened && !draft;
  useAddNoteAction(
    collapsed && !readOnly && !signed,
    () => {
      focusOnOpen.current = true;
      setOpened(true);
    },
    id,
  );
  useEffect(() => {
    if (!opened || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    // Within this line (not by id: for one render the header's "Add note" still carries the same anchor).
    lineRef.current?.querySelector<HTMLElement>('textarea, [contenteditable="true"]')?.focus();
  }, [opened]);

  // Focus leaving the line (not moving inside it, e.g. into its phrase menu) saves what was typed.
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    // Frozen (the field left because the note was signed): nothing is written after the sign; the effect above holds it.
    if (readOnly) return;
    const leftEmpty = !segsText(current).trim() && !fill;
    flush();
    if (leftEmpty) setOpened(false);
  };

  const resolve = useCallback((q: Parameters<typeof resolveChart>[0]) => resolveChart(q, chartReads), [chartReads]);
  const offered = useMemo(
    () => (macros ? suggestedFirst(offeredPhrasesIn(macros, editors, { pieces }), suggested ?? []) : []),
    [macros, editors, pieces, suggested],
  );
  const items = useMemo(() => lineCommandItems(toCommandItems(offered), author), [offered, author]);
  const commands: NoteFieldCommands | undefined =
    macros && !readOnly
      ? {
          items,
          role: author,
          onInsert: (item: CommandMenuItem) => {
            const macro = offered.find((m) => m.id === item.id);
            if (!macro) return undefined;
            if (carriesPieces(macro)) {
              // Where the `.` token starts, read before NoteFieldLine removes it: the phrase lands there (PROD-3148).
              setFill(startPhraseFill(id, macro, resolve, triggerAnchorIn(id)));
              return undefined;
            }
            return phrasePlainText(macro);
          },
        }
      : undefined;

  // Chart readout: the picker is open while the text ends with "\", there is something to offer, and Escape has not closed it
  // for this very text. A pick or a removal is saved at once, like a completed phrase.
  const [readoutHi, setReadoutHi] = useState(0);
  const [readoutClosedAt, setReadoutClosedAt] = useState<string | null>(null);
  const readoutAsked = readoutOn && !readOnly && !!chartReads && READOUT_TRIGGER.test(bodyText) && readoutClosedAt !== bodyText;
  // Resolved only while asked for (the compiler memoizes the rest).
  const sources = readoutAsked ? readoutSources(resolve, readouts.map((r) => r.query)) : [];
  const readoutOpen = sources.length > 0;
  const hi = Math.min(readoutHi, Math.max(sources.length - 1, 0));
  const commitNow = (next: LineSeg[]) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    latest.current = { ...latest.current, draft: null };
    setDraft(null);
    onCommit(next);
  };
  const pickReadout = (source: ReadoutSource) => {
    commitNow(joinReadouts(rebaseSegs(body, withoutTrigger(bodyText)), [...readouts, newReadout(source.query, resolve)]));
    setReadoutHi(0);
    document.getElementById(id)?.focus();
  };
  const removeReadout = (index: number) => commitNow(joinReadouts(body, readouts.filter((_, i) => i !== index)));
  const onReadoutKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only keys typed in the field itself: Enter on a card's X or in the phrase panel is theirs.
    if (!readoutOpen || !(e.target instanceof HTMLElement) || e.target.id !== id) return;
    const keys: Record<string, () => void> = {
      ArrowDown: () => setReadoutHi(Math.min(hi + 1, sources.length - 1)),
      ArrowUp: () => setReadoutHi(Math.max(hi - 1, 0)),
      Enter: () => pickReadout(sources[hi]),
      Escape: () => setReadoutClosedAt(bodyText),
    };
    const run = keys[e.key];
    if (!run) return;
    e.preventDefault();
    e.stopPropagation();
    run();
  };

  // Chart links still unresolved re-read the page's latest reads on every render (as the template lines do).
  const liveFill = fill ? { ...fill, ops: withFreshCharts(fill.ops, resolve) } : null;
  const backToLine = () => document.getElementById(id)?.focus();
  // Frozen and empty: not asked is silence (NoteFieldLine draws nothing), so no empty wrapper either. A block
  // narrative that is empty and closed draws nothing at all (its "Add note" is in the block's header).
  const heldNotice =
    held !== null && readOnly ? (
      <p className="m-0 font-sans text-size-sm text-text-secondary" role="status" data-held-draft>
        {SIGNED_NOT_SAVED} <span className="whitespace-pre-wrap text-text-primary">{held}</span>
      </p>
    ) : null;
  if (collapsed || (readOnly && !segsText(current) && !footnote && !heldNotice)) return null;

  return (
    <div ref={lineRef} className="flex flex-col gap-2" onBlur={onBlur} onKeyDownCapture={onReadoutKey} data-inline-note-line={id}>
      <NoteFieldLine id={id} label={label} value={bodyText} onChange={readOnly ? undefined : change} readOnly={readOnly} commands={commands} />
      {readoutOpen && (
        // In the line's value column, under the text (the note's one label column, D-62).
        <NoteLine label="">
          <ChartReadoutMenu id={`${id}-chart`} sources={sources} highlighted={hi} onPick={pickReadout} onHighlight={setReadoutHi} />
        </NoteLine>
      )}
      {readouts.length > 0 && (
        <NoteLine label="" valueClassName="flex flex-col gap-2">
          {readouts.map((piece, i) => (
            <ChartReadoutCard key={piece.id} piece={piece} onRemove={readOnly ? undefined : () => removeReadout(i)} />
          ))}
        </NoteLine>
      )}
      {!readOnly && liveFill && (
        <PhraseOccurrence
          key={liveFill.key}
          draft={liveFill}
          onChange={(ops) => setFill({ ...liveFill, ops })}
          onRemove={() => {
            setFill(null);
            backToLine();
          }}
          onRefreshChart={(index) => {
            const next = withRefreshedChart(liveFill.ops, index, resolve);
            setFill({ ...liveFill, ops: next.ops });
            return { changed: next.changed, pulledAt: next.pulledAt };
          }}
          renderChartPiece={renderChartPiece}
          onComplete={() => {
            // The completed phrase lands WITH its pieces where `.` was typed (PROD-3148), and is saved at once.
            // Into the text only: the chart readouts stay after it (`body` is the whole line when it holds none).
            const placed = insertPhrase(body, liveFill.ops, liveFill.anchor ? placeAt(liveFill.anchor, bodyText) : undefined, liveFill.anchor?.pad);
            const next = joinReadouts(placed, readouts);
            if (timer.current) clearTimeout(timer.current);
            timer.current = null;
            latest.current = { ...latest.current, draft: null };
            setDraft(null);
            setFill(null);
            onCommit(next);
            backToLine();
          }}
        />
      )}
      {heldNotice}
      {footnote}
    </div>
  );
}
